import GIF from "gif.js";
import * as spine from "@esotericsoftware/spine-webgl";
import { SpinePreviewApp } from "../../../../../lib/SpinePreviewApp";
import {
  captureAllFrames,
  captureStaticStackFramesOnly,
} from "./exportFrames";
import type { StaticStackItem } from "../../../../../lib/staticLayerStack";

/**
 * gif.js 只支持「单一键色透明」，会丢弃 canvas alpha。
 * 透明像素涂成该色，并传给 GIF({ transparent })；选用少见绿色降低误伤角色颜色的概率。
 */
const GIF_TRANSPARENT_KEY = {
  hex: 0x00ff00,
  r: 0,
  g: 255,
  b: 0,
} as const;

/** alpha 低于此阈值视为背景透明（GIF 无半透明） */
const GIF_ALPHA_THRESHOLD = 128;

export interface ExportGifOptions {
  app?: SpinePreviewApp | null;
  spineCanvas?: spine.SpineCanvas | null;
  outputSize?: number;
  fps?: number;
  durationMs?: number;
  /** gif.js 编码质量，1 最高，默认 10 */
  gifQuality?: number;
  transparentBackground?: boolean;
  highQualityCapture?: boolean;
  onProgress?: (percent: number) => void;
  staticStackItems?: StaticStackItem[];
}

/**
 * 将 canvas RGBA 转为 gif.js 可用的键色透明帧：透明区涂键色，不透明区避免撞色。
 */
function canvasToGifKeyedImageData(canvas: HTMLCanvasElement): ImageData {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    throw new Error("无法读取导出帧（2d context）");
  }
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const { r, g, b } = GIF_TRANSPARENT_KEY;

  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < GIF_ALPHA_THRESHOLD) {
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = 255;
      continue;
    }

    // 不透明像素若碰巧等于键色，微调以免被当成透明
    if (data[i] === r && data[i + 1] === g && data[i + 2] === b) {
      data[i + 2] = b > 0 ? b - 1 : 1;
    }
    data[i + 3] = 255;
  }

  return imageData;
}

/**
 * 从当前 SpinePreviewApp 导出 GIF 动图；无主 Spine 时仅静态叠层也可导出。
 */
export function exportGif({
  app,
  spineCanvas,
  outputSize = 512,
  fps = 50,
  durationMs,
  gifQuality = 10,
  transparentBackground = true,
  highQualityCapture = true,
  onProgress,
  staticStackItems,
}: ExportGifOptions): Promise<Blob> {
  return new Promise(async (resolve, reject) => {
    try {
      const gifFrameDelay = Math.round(1000 / fps / 10) * 10;
      const gifFps = 1000 / gifFrameDelay;

      const useSpine =
        !!app &&
        !!spineCanvas &&
        app.instances.length > 0;
      const useStaticOnly =
        !useSpine &&
        !!staticStackItems?.some((i) => i.kind === "img");

      if (!useSpine && !useStaticOnly) {
        reject(
          new Error("没有可渲染的 Spine 或静态图层"),
        );
        return;
      }

      const { frames } = useSpine
        ? await captureAllFrames({
            app: app!,
            spineCanvas: spineCanvas!,
            outputSize,
            fps: gifFps,
            durationMs,
            onFrameProgress: (p) => onProgress?.(p * 0.5),
            staticStackItems,
            transparentBackground,
            highQualityCapture,
          })
        : await captureStaticStackFramesOnly({
            staticStackItems: staticStackItems!,
            outputSize,
            fps: gifFps,
            durationMs: durationMs ?? 2000,
            onFrameProgress: (p) => onProgress?.(p * 0.5),
            transparentBackground,
          });

      const gif = new GIF({
        workers: 4,
        quality: gifQuality,
        width: outputSize,
        height: outputSize,
        // @types/gif.js 标成 string，运行时需 number（0xRRGGBB）
        transparent: transparentBackground
          ? (GIF_TRANSPARENT_KEY.hex as unknown as string)
          : null,
        workerScript: `${process.env.PUBLIC_URL || ""}/gif.worker.js`,
      });

      gif.on("finished", (blob: Blob) => resolve(blob));
      gif.on("progress", (p: number) => onProgress?.(0.5 + p * 0.5));

      for (const frame of frames) {
        const source = transparentBackground
          ? canvasToGifKeyedImageData(frame.canvas)
          : frame.canvas.getContext("2d")!;

        gif.addFrame(source, {
          copy: true,
          delay: frame.frameDelay,
          // 透明动画：每帧绘制前恢复背景，避免残影叠在透明区
          ...(transparentBackground ? { dispose: 2 } : {}),
        });
      }

      gif.render();
    } catch (err) {
      reject(err);
    }
  });
}
